import { PERMISSIONS, type Offer, type OfferDetail } from '@ooh/contracts';
import { canConfirmCampaign, canRecordSale } from '@ooh/screens';

export function deriveWorkspacePermissionFlags(permissions: readonly string[] | null) {
  return {
    canApproveAny: permissions?.includes(PERMISSIONS.OFFER_APPROVE_ANY) ?? false,
    canManageAny: permissions?.includes(PERMISSIONS.OFFER_VIEW_ALL) ?? false,
    canSetStatusAny: permissions?.includes(PERMISSIONS.OFFER_SET_STATUS_ANY) ?? false,
  };
}

/**
 * An offer is read-only when it has been trashed or CANCELLED.
 * Non-DRAFT status (SENT, ACCEPTED, CONFIRMED) no longer blocks editing —
 * the API guards were removed in task 2.3; the edit-gating UX (EditGateBanner)
 * informs users that edits land in the next published version. CANCELLED is a
 * terminal status that disables editing outright rather than warning that
 * edits create a new version (offer-lifecycle-reversibility).
 */
export function isOfferReadOnly(offer: Pick<Offer, 'trashedAt' | 'status'>): boolean {
  return offer.trashedAt != null || offer.status === 'CANCELLED';
}

type WorkspaceOffer = Pick<
  OfferDetail,
  | 'authorId'
  | 'salespersonId'
  | 'status'
  | 'trashedAt'
  | 'effectiveAccess'
  | 'hasUnsecuredAcceptedUnits'
>;

/**
 * Editing affordances follow the caller's server-reported effective access
 * (offer-draft-management-ui / "Workspace action gating follows the caller's
 * reported access"), never local authorship. `effectiveAccess` already folds
 * in the offer's owner (author or assigned salesperson), any `offer.view_all`
 * holder, and an editing share grant — see
 * apps/api/src/offers/offer-access.ts's `resolveOfferRecordAccess`, the
 * single implementation of this rule.
 */
function deriveCanEdit(
  offer: Pick<OfferDetail, 'trashedAt' | 'status' | 'effectiveAccess'>,
): boolean {
  return offer.effectiveAccess === 'view+edit' && !isOfferReadOnly(offer);
}

export function deriveWorkspaceShellAccess({
  canApproveAny,
  canManageAny,
  canSetStatusAny,
  offer,
  userId,
}: {
  canApproveAny: boolean;
  canManageAny: boolean;
  canSetStatusAny: boolean;
  offer: WorkspaceOffer;
  userId: number | null;
}) {
  const isAuthor = userId != null && offer.authorId === userId;
  const isAssignedSalesperson = userId != null && offer.salespersonId === userId;
  const canEdit = deriveCanEdit(offer);

  return {
    canAdminChangeStatus: canSetStatusAny,
    canConfirmCampaign: canConfirmCampaign(
      offer.status,
      canEdit,
      canApproveAny,
      offer.hasUnsecuredAcceptedUnits,
    ),
    canEdit,
    canRecordSale: canRecordSale(offer.status, canEdit, canApproveAny),
    hasUnsecuredUnits: offer.hasUnsecuredAcceptedUnits,
    // Sharing management and trash stay limited to the owner side (creator,
    // assigned salesperson) and offer.view_all — NOT read from
    // effectiveAccess, which would over-grant them to a share holder with
    // editing access. Matches what the server allows
    // (offer-draft-management-ui Non-Goals: "No change to who may manage
    // sharing or move an offer to the trash").
    canManageSharing: isAuthor || isAssignedSalesperson || canManageAny,
    canTrash: isAuthor || isAssignedSalesperson || canManageAny,
  };
}
